import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Skill, Stage } from '../../store/missions/types';
import { autoId } from '../../utils/UidGenerator';
import { Exercise } from '../../store/exercises/types';
import { Task } from '../../store/tasks/types';
import { requestSaveStage } from '../../store/missions/actions';
import { requestSaveExercises } from '../../store/exercises/actions';
import { requestSaveTasks } from '../../store/tasks/actions';
import { LanguagesBar } from '../LanguagesBar';
import { Button, Grid, TextField, TextareaAutosize } from '@mui/material';
import AddImage from '../AddImage/AddImage';
import SurveyExercise from '../StageZero/SurveyExercise';
import SnackbarMessage from '../SnackbarMessage/SnackbarMessage';
import SaveIcon from '@mui/icons-material/Save';
import strings from '../../resources/strings';
import { storage } from '../../resources/storage';
import '../StageZero/StageZero.scss';
import { StageSurveyBaseProps, ValueGroups } from '../StageZero/types';
import { Report } from '../StageZero/types';
import ReportContainer from '../StageZero/Report/ReportContainer';
import ValueGroupsContainer from '../StageZero/Values/ValuesContainer';

const StageSurveyBase: React.FC<StageSurveyBaseProps> = (props) => {
    const dispatch = useDispatch();
    const [stage, setStage] = useState<Stage>(props.inititialStage);
    const [language, setLanguage] = useState('pl');
    const [exercises, setExercises] = useState<Exercise[]>([]);
    const [tasks, setTasks] = useState<Task[]>([]);
    const [submit, setSubmit] = useState<boolean>(false);
    const [success, setSuccess] = useState(false);
    const [reports, setReports] = useState<Report[]>(props.reports ?? []);
    const [valueGroups, setValueGroups] = useState<ValueGroups[]>(props.valueGroups ?? []);
    const [maxPoints, setMaxPoints] = useState<number | undefined>(props.maxPoints);

    const createNewExercise = (skill: Skill) => {
        return {
            id: autoId(),
            backpack: false,
            visibleSavedExercises: false,
            description: {},
            image: '',
            missionsToUnlock: [],
            name: skill.name,
            renewalPercent: 0,
            reward: '',
            tasks: [],
            skills: [],
        } as Exercise;
    };

    const getSkillsForExercise = (exercise: Exercise) => {
        const keys = tasks
            .filter((task) => exercise.tasks?.includes(task.id))
            .map((task) => {
                return (
                    task?.options?.map((option) => {
                        return Object.keys(option.skills as object);
                    }) ?? []
                );
            })
            .flat()[0];
        return keys ? Array.from(keys).filter((x) => x != 'none') : [];
    };

    const handleSubmit = () => {
        if (
            stage.order === 0 &&
            (!maxPoints ||
                maxPoints < 0 ||
                reports.some((x) => (x.pointsFrom ?? 0) < 0) ||
                reports.some((x) => (x.pointsFrom ?? 0) < 0))
        )
            return;

        setStage({
            ...stage,
            reports: reports,
            valueGroups: valueGroups,
            stageExitSurvey: props.stageExitSurvey,
            exercises:
                exercises.map((exercise) => {
                    return exercise.id ?? '';
                }) ?? [],
        });
        setExercises(
            exercises.map((exercise) => {
                return {
                    ...exercise,
                    skills: getSkillsForExercise(exercise),
                };
            }),
        );
        setSubmit(true);
    };

    useEffect(() => {
        if (submit) {
            dispatch(requestSaveStage(stage, props.missionId));
            dispatch(requestSaveExercises(exercises));
            dispatch(requestSaveTasks(tasks));
            setSubmit(false);
            setSuccess(true);
        }
    }, [submit]);

    const updateStage = (e: any) => {
        const { name, value } = e.target;
        const splitetName = name.split('_');
        const property = splitetName[0];
        const language = splitetName[1];
        const oldValue = Object.entries(stage).find((x) => x[0] === property)?.[1] as object;
        const newValue = { ...oldValue, [language]: value };
        setStage({
            ...stage,
            [property]: newValue,
        });
    };

    const updateMaxPoints = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        const splitetName = name.split('_');
        const property = splitetName[0];
        setStage({
            ...stage,
            [property]: Number(value),
        });
        setMaxPoints(value.length > 0 ? Number(value) : undefined);
    };

    useEffect(() => {
        if (props.exercisesData.length > 0) setExercises(props.exercisesData);
        else
            setExercises(
                props.skills?.map((skill) => {
                    return createNewExercise(skill);
                }) ?? [],
            );
    }, [props.exercisesData]);

    return (
        <div className="stage-zero-container">
            <LanguagesBar
                languages={props.languages}
                selectedLanguage={language}
                handleChange={setLanguage}
            ></LanguagesBar>
            <form className="form-container" onSubmit={handleSubmit}>
                <div className="body-container">
                    <Grid container spacing={2}>
                        {!props.stageExitSurvey && (
                            <>
                                {props.languages.map((lang) => (
                                    <Grid
                                        item
                                        xs={6}
                                        key={lang.code}
                                        style={{ display: lang.code === language ? 'block' : 'none' }}
                                    >
                                        <>
                                            <TextField
                                                fullWidth
                                                key={`name_${lang.code}`}
                                                defaultValue={stage.name?.[lang.code]}
                                                variant="outlined"
                                                required={true}
                                                type="text"
                                                id={`name_${lang.code}`}
                                                label="Chapter title"
                                                name={`name_${lang.code}`}
                                                onChange={updateStage}
                                            ></TextField>
                                            <TextareaAutosize
                                                key={`description_${lang.code}`}
                                                defaultValue={stage.description?.[lang.code]}
                                                id={`description_${lang.code}`}
                                                placeholder="Chapter description"
                                                name={`description_${lang.code}`}
                                                maxRows={6}
                                                onChange={updateStage}
                                            ></TextareaAutosize>
                                        </>
                                    </Grid>
                                ))}
                            </>
                        )}

                        {!props.stageExitSurvey && (
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    key={`maxPoints`}
                                    defaultValue={maxPoints}
                                    variant="outlined"
                                    type="number"
                                    id={`maxPoints`}
                                    label="Maximum points"
                                    name={`maxPoints`}
                                    onChange={updateMaxPoints}
                                    error={!maxPoints || maxPoints < 0}
                                    helperText={
                                        !maxPoints
                                            ? 'Field cannot be empty'
                                            : maxPoints < 0
                                            ? 'Value must be greater then zero'
                                            : ''
                                    }
                                ></TextField>
                            </Grid>
                        )}
                        {props.languages.map((lang) => (
                            <Grid
                                item
                                xs={6}
                                key={lang.code}
                                style={{ display: lang.code === language ? 'block' : 'none' }}
                            >
                                {!props.stageExitSurvey && (
                                    <>
                                        <ReportContainer
                                            language={language}
                                            languages={props.languages}
                                            reports={reports}
                                            setReports={setReports}
                                            deleteEnabled={props.deleteEnabled}
                                        />
                                        <ValueGroupsContainer
                                            language={language}
                                            languages={props.languages}
                                            valueGroups={valueGroups}
                                            setValueGroups={setValueGroups}
                                            deleteEnabled={props.deleteEnabled}
                                        />
                                        <h2>
                                            {props.mission?.isSurveyDifferent
                                                ? strings.missions.stageExitSurvey.entrySurvey
                                                : strings.missions.stageZero.surveyLabel}
                                        </h2>
                                    </>
                                )}
                                {props.stageExitSurvey && <h2>{strings.missions.stageExitSurvey.exitSurvey}</h2>}
                                <TextareaAutosize
                                    key={`surveyDescription_${lang.code}`}
                                    defaultValue={stage.surveyDescription?.[lang.code]}
                                    id={`surveyDescription_${lang.code}`}
                                    placeholder={
                                        props.stageExitSurvey ? 'Last screen description' : 'First screen description'
                                    }
                                    name={`surveyDescription_${lang.code}`}
                                    maxRows={6}
                                    onChange={updateStage}
                                ></TextareaAutosize>
                            </Grid>
                        ))}
                        <Grid item xs={6}>
                            <AddImage
                                path={storage.stages}
                                showMessage={false}
                                customName={`${stage.id}`}
                                sendOnDrop={true}
                                setGetImgAddress={(url: string) => setStage({ ...stage, image: url })}
                                actualEventFile={stage.image}
                            ></AddImage>
                        </Grid>
                        <Grid item xs={12}>
                            <div className="skills-container">
                                {exercises?.map((exercise) => {
                                    return (
                                        <div key={`exercise_${exercise.id}`}>
                                            <SurveyExercise
                                                name={exercise.name}
                                                language={language}
                                                languages={props.languages}
                                                skills={props.skills}
                                                tasksData={props.tasksData}
                                                tasks={tasks}
                                                exercises={exercises}
                                                currentExercise={exercise}
                                                taskType={props.taskType}
                                                setTasks={setTasks}
                                                setExercises={setExercises}
                                            ></SurveyExercise>
                                        </div>
                                    );
                                })}
                            </div>
                        </Grid>
                    </Grid>
                    <SnackbarMessage
                        serverity="success"
                        message={success ? strings.tasks.successMessage : ''}
                        clearMessage={() => {
                            setSuccess(false);
                        }}
                    ></SnackbarMessage>
                </div>
                <div className="button-container">
                    <Button
                        variant="outlined"
                        startIcon={<SaveIcon />}
                        sx={{ backgroundColor: '#E3F2FD', color: '#2196F3' }}
                        className="save-button"
                        type="button"
                        onClick={handleSubmit}
                    >
                        {strings.newsAndEvents.buttons.save}
                    </Button>
                </div>
            </form>
        </div>
    );
};

export default StageSurveyBase;
